import { CacheWriteType } from '../../../api/hooks/binding/cache-writer.binding';
import {
  ScriptActionBindingContext,
  ScriptActionBindingResult,
} from '../../../api/hooks/binding/script-action.binding';
import { ProgramState } from '../../../components/machine/machine-state';
import { ApplicationConfig } from '../../../config/config';
import { Waypoint } from '../../../util/machine-variable.utils';
import { PartUtil } from '../../../util/part.util';
import { Pose } from '../../../util/pose.util';
import {
  OperationResult,
  RobotOperationalContext,
  RobotOperations,
  URContext,
} from '../../robot.types';

/**
 * Universal Robots operations implementation using URScript
 * Contains hardcoded URScript templates for all robot operations
 */
export class UROperations implements RobotOperations {
  // One-shot operations binding (closes immediately after execution)
  private sendScriptOneShot: (
    machineId: string,
    context: ScriptActionBindingContext
  ) => Promise<ScriptActionBindingResult | undefined>;

  // Long-running operations binding (stays open for continuous operations)
  private sendScriptLongRunning: (
    machineId: string,
    context: ScriptActionBindingContext
  ) => Promise<ScriptActionBindingResult | undefined>;

  private closeScriptBinding: () => void;
  private writeToCacheBinding: (machineId: string, data: any) => Promise<any>;

  constructor(
    sendScriptOneShot: (
      machineId: string,
      context: ScriptActionBindingContext
    ) => Promise<ScriptActionBindingResult | undefined>,
    sendScriptLongRunning: (
      machineId: string,
      context: ScriptActionBindingContext
    ) => Promise<ScriptActionBindingResult | undefined>,
    closeScriptBinding: () => void,
    writeToCacheBinding: (machineId: string, data: any) => Promise<any>
  ) {
    this.sendScriptOneShot = sendScriptOneShot;
    this.sendScriptLongRunning = sendScriptLongRunning;
    this.closeScriptBinding = closeScriptBinding;
    this.writeToCacheBinding = writeToCacheBinding;

    // Bind all public methods to preserve 'this' context when destructured
    this.moveToPoint = this.moveToPoint.bind(this);
    this.startFineTune = this.startFineTune.bind(this);
    this.fineTuneMoveTo = this.fineTuneMoveTo.bind(this);
    this.halt = this.halt.bind(this);
    this.stopProgram = this.stopProgram.bind(this);
    this.close = this.close.bind(this);
  }

  private async writeToCache(machineId: string, data: any): Promise<void> {
    await this.writeToCacheBinding(machineId, data);
  }

  private convertWaypointToScriptPose(waypoint: Waypoint): string {
    return `p[${[
      waypoint.x,
      waypoint.y,
      waypoint.z,
      waypoint.rx,
      waypoint.ry,
      waypoint.rz,
    ].join(',')}]`;
  }

  async moveToPoint(
    machineId: string,
    point: Waypoint,
    qnear: number[],
    stickout: number,
    context: RobotOperationalContext & URContext
  ): Promise<OperationResult> {
    try {
      const { tcp, executingIndex } = context;
      const convertedPoint = this.convertWaypointToScriptPose(point);
      const convertedTcp = this.convertWaypointToScriptPose(tcp);

      const script = [
        `def move_to_point():`,
        ` local LIVE_EXECUTION_REG = 24`,
        ` local PROGRAM_END = -1`,
        ` local START_DISTANCE_THRESHOLD = 0.0005`,
        ` local THREAD_INACTIVE = -999`,
        ` def moveHere(pose, jointPosition, stickout, tcp, liveExecutionStep):`,
        ` 	set_tcp(tcp)`,
        ` 	pose = pose_trans(pose, p[0, 0, -stickout / 1000, 0, 0, 0])`,
        ` 	setMoveToWaypoint(pose, jointPosition)`,
        ` 	set_standard_digital_out(0, True) #REDUCED MODE FOR SAFETY`,
        ` 	set_standard_digital_out(1, True) #REDUCED MODE FOR SAFETY`,
        ` 	global reachedPoint = False`,
        ` 	local btn_thrd = run flashBlueButton()`,
        ` 	local moveHereThreadID = THREAD_INACTIVE`,
        ` 	write_output_integer_register(LIVE_EXECUTION_REG, liveExecutionStep) # Setting Live Execution step to register`,
        ` 	if (pose_dist(get_actual_tcp_pose(), pose) < START_DISTANCE_THRESHOLD):`,
        ` 		reachedPoint = True`,
        ` 	end`,
        ` 	textmsg("Entering Move Here loop, reached point: ", reachedPoint)`,
        ` 	while (not reachedPoint):`,
        ` 		while (not get_standard_digital_in(1)):`,
        ` 			sync()`,
        ` 			if (get_tool_digital_in(0)):`,
        ` 				textmsg("Operator using Free Drive in Move Here")`,
        ` 				freedrive_mode()`,
        ` 				set_tool_digital_out(0, False)`,
        ` 				while (get_tool_digital_in(0)):`,
        ` 					sync()`,
        ` 				end`,
        ` 				end_freedrive_mode()`,
        ` 				set_tool_digital_out(0, True)`,
        ` 			end`,
        ` 		end`,
        ` 		if (get_standard_digital_in(1) and not reachedPoint):`,
        ` 			textmsg("Operator has pressed blue button to move to point")`,
        ` 			set_standard_digital_out(0, False) #REDUCED MODE FOR SAFETY`,
        ` 			set_standard_digital_out(1, False) #REDUCED MODE FOR SAFETY`,
        ` 			set_tool_digital_out(0, False)`,
        ` 			while (get_standard_digital_in(1) and not reachedPoint):`,
        ` 				if (moveHereThreadID == THREAD_INACTIVE):`,
        ` 					moveHereThreadID = run moveToPointThrd()`,
        ` 				end`,
        ` 				sync()`,
        ` 			end`,
        ` 			if (not get_standard_digital_in(1) or reachedPoint):`,
        ` 				kill moveHereThreadID`,
        ` 				set_tool_digital_out(0, True)`,
        ` 				stopj(5)`,
        ` 				textmsg("Ending move to point")`,
        ` 				moveHereThreadID = THREAD_INACTIVE`,
        ` 				set_standard_digital_out(0, True) #REDUCED MODE FOR SAFETY`,
        ` 				set_standard_digital_out(1, True) #REDUCED MODE FOR SAFETY`,
        ` 				set_tcp(tcp)`,
        ` 				sleep(0.05)`,
        ` 			end`,
        ` 		end`,
        ` 	end`,
        ` 	kill btn_thrd`,
        ` 	set_standard_digital_out(2, False)`,
        ` 	write_output_integer_register(LIVE_EXECUTION_REG, PROGRAM_END)`,
        ` 	halt`,
        ` end`,
        ` thread moveToPointThrd():`,
        ` 	while (True):`,
        ` 		local pose = getMoveToWaypoint()`,
        ` 		local qnear = getMoveToJointPosition()`,
        ` 		movej(get_inverse_kin(pose, qnear), a = d2r(40), v = d2r(20))`,
        ` 		reachedPoint = True`,
        ` 		stopj(5)`,
        ` 	end`,
        ` end`,
        ` thread flashBlueButton():`,
        ` 	while (True):`,
        ` 		set_standard_digital_out(2, False)`,
        ` 		sleep(1)`,
        ` 		set_standard_digital_out(2, True)`,
        ` 		sleep(1)`,
        ` 	end`,
        ` end`,
        ` local moveToPose = p[0, 0, 0, 0, 0, 0]`,
        ` local moveToJointPosition = [0, 0, 0, 0, 0, 0]`,
        ` def setMoveToWaypoint(pose, qnear):`,
        ` 	moveToPose = pose`,
        ` 	moveToJointPosition = qnear`,
        ` end`,
        ` def getMoveToWaypoint():`,
        ` 	return moveToPose`,
        ` end`,
        ` def getMoveToJointPosition():`,
        ` 	return moveToJointPosition`,
        ` end`,
        ` moveHere(${convertedPoint}, [${qnear.join(
          ', '
        )}], ${stickout}, ${convertedTcp}, ${executingIndex})`,
        `end`,
      ].join('\n');

      const result = await this.sendScriptOneShot(machineId, {
        script,
        actionName: 'moveToPoint',
        validProgramStates: [
          ProgramState.PROGRAM_STOPPED,
          ProgramState.PROGRAM_STOPPING,
        ],
      });

      return {
        success: result?.success ?? false,
        error: result?.success
          ? undefined
          : 'Failed to execute move to point operation',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async startFineTune(
    machineId: string,
    context: RobotOperationalContext & { onStop?: () => void }
  ): Promise<OperationResult> {
    try {
      const { tcp, executingIndex } = context;

      const script = this.getFineTuneScript(tcp, executingIndex);

      const haltScript = this.getFineTuneHaltScript();

      const result = await this.sendScriptLongRunning(machineId, {
        script,
        actionName: 'startFineTune',
        onStop: {
          validProgramStates: [
            ProgramState.PROGRAM_PLAYING,
            ProgramState.PROGRAM_PAUSED,
          ],
          script: haltScript,
        },
      });

      return {
        success: result?.success ?? false,
        error: result?.success
          ? undefined
          : 'Failed to start fine tune operation',
        data: { onStop: context.onStop },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async fineTuneMoveTo(
    machineId: string,
    pose: Pose
  ): Promise<OperationResult> {
    try {
      const poses = [PartUtil.convertPoseToWaypoint(pose)];

      await this.writeToCache(machineId, {
        requests: [
          {
            path: 'finetune.poses',
            value: poses,
            type: CacheWriteType.MEMORY,
          },
        ],
      });

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async halt(
    machineId: string,
    // _operationName parameter kept for API compatibility but not used
    _operationName?: string
  ): Promise<OperationResult> {
    try {
      // Use bare halt command for consistency with stopProgram
      const script = `halt`;

      const result = await this.sendScriptOneShot(machineId, {
        script,
        actionName: 'halt',
        validProgramStates: [
          ProgramState.PROGRAM_PLAYING,
          ProgramState.PROGRAM_PAUSED,
        ],
      });

      return {
        success: result?.success ?? false,
        error: result?.success ? undefined : 'Failed to halt operation',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async stopProgram(machineId: string): Promise<OperationResult> {
    try {
      // Use bare halt command for backwards compatibility with previous implementation
      const script = `halt`;

      const result = await this.sendScriptOneShot(machineId, {
        script,
        actionName: 'stopProgram',
        validProgramStates: [
          ProgramState.PROGRAM_PLAYING,
          ProgramState.PROGRAM_PAUSED,
        ],
      });

      return {
        success: result?.success ?? false,
        error: result?.success ? undefined : 'Failed to stop program',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  private getFineTuneScript = (
    tcp: Waypoint,
    executingIndex: number
  ): string => {
    const convertedTcp = this.convertWaypointToScriptPose(tcp as Waypoint);

    const url = new URL(
      `http://${ApplicationConfig.robot.rpc.host}:${ApplicationConfig.robot.rpc.port}`
    );

    url.pathname = ApplicationConfig.robot.rpc.path;

    const script = [
      `def fine_tune():`,
      ` local LIVE_EXECUTION_REG = 24`,
      ` local client = rpc_factory("xmlrpc", "${url}")`,
      ` def moveTo(tcp, liveExecutionStep):`,
      `   set_tcp(tcp)`,
      ` 	write_output_integer_register(LIVE_EXECUTION_REG, -1)`,
      `   local prevHaltState = False`,
      `   local defaultPoses = [p[0, 0, 0, 0, 0, 0]]`,
      `   local previousPoses = defaultPoses`,
      `   client.setCacheValue("finetune.halt", prevHaltState, False)`,
      `   client.setCacheValue("finetune.poses", previousPoses, False)`,
      `   while (True):`,
      `     if (get_tool_digital_in(0)):`,
      `       freedrive_mode()`,
      `       set_tool_digital_out(0, False)`,
      `       while (get_tool_digital_in(0)):`,
      `         sync()`,
      `       end`,
      `       end_freedrive_mode()`,
      `       set_tool_digital_out(0, True)`,
      `     end`,
      `     local cachedPoses = client.getCacheValue("finetune.poses", previousPoses)`,
      `     if (cachedPoses != previousPoses and cachedPoses[0] != defaultPoses[0]):`,
      `       previousPoses = cachedPoses`,
      `       movel(cachedPoses[0], a=0.05, v=0.05, t=0, r=0)`,
      `       stopl(10)`,
      `     end`,
      `     local cachedHalt = client.getCacheValue("finetune.halt", prevHaltState)`,
      `     if (cachedHalt != prevHaltState):`,
      `       halt`,
      `     end`,
      `     sleep(.1)`,
      `   end`,
      ` end`,
      ` moveTo(${convertedTcp}, ${executingIndex})`,
      `end`,
    ];

    return script.join('\n');
  };

  private getFineTuneHaltScript = () => {
    return this.getHaltScript('fine_tune');
  };

  private getHaltScript = (scriptName: string) => {
    return [`def ${scriptName}():`, `halt`, `end`].join('\n');
  };

  close(): void {
    this.closeScriptBinding();
  }
}
